import React, { useState, useEffect, useRef } from 'react'
import Topbar from '../components/Layout/Topbar'
import { Sidebar } from '../components/Layout/Sidebar'
import { Button, Checkbox } from '@material-tailwind/react'
import { supabase } from '../supabase.config'
import { useNavigate } from 'react-router-dom'
import toast, { Toaster } from 'react-hot-toast'
import { FaDownload, FaFilePdf } from 'react-icons/fa'
import Loader from '../components/Loader'
import RecieverCopyContent from '../components/DigitalLR/RecieverCopyContent'

const BulkWaybillDownload = () => {
  const navigate = useNavigate()
  const [orders, setOrders] = useState([])
  const [selectedOrders, setSelectedOrders] = useState(new Set())
  const [loading, setLoading] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const waybillRefs = useRef({})

  const parseJSON = (v) => {
    if (!v) return null
    if (typeof v === 'object') return v
    try { return JSON.parse(v) } catch { return null }
  }

  const computeChargeable = (dims) => {
    try {
      return Math.ceil((dims || []).reduce((sum, d) => sum + (((Number(d.length) || 0) * (Number(d.width) || 0) * (Number(d.height) || 0)) / 4500) * (Number(d.count) || 0), 0))
    } catch { return 0 }
  }

  const fetchOrders = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1000)

      if (error) {
        toast.error('Failed to fetch orders: ' + error.message)
        return
      }

      const mapped = (data || []).map(row => {
        const pickup = parseJSON(row.pickup_address) || {}
        const drop = parseJSON(row.delivery_address) || {}
        const dims = parseJSON(row.dimensions) || []
        const pkg = parseJSON(row.package_details) || {}

        return {
          id: row.id,
          lrno: row.tracking_number || row.order_number,
          orderID: row.order_id || row.order_number || '',
          createdAt: row.created_at,
          pickup_location: pickup,
          dropoff_location: drop,
          invoices: pkg.invoices || [],
          dimensions: dims,
          weight: row.weight,
          totalChargeableWeight: computeChargeable(dims),
          userName: pkg.userName || pickup?.pickupName?.companyName || pickup?.companyName || pickup?.personName || ''
        }
      })

      setOrders(mapped)
    } catch (error) {
      console.error('Error fetching orders:', error)
      toast.error('Error fetching orders')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchOrders()
  }, [])

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedOrders(new Set(orders.map(o => o.id)))
    } else {
      setSelectedOrders(new Set())
    }
  }

  const handleSelectOrder = (orderId, checked) => {
    const newSelected = new Set(selectedOrders)
    if (checked) {
      newSelected.add(orderId)
    } else {
      newSelected.delete(orderId)
    }
    setSelectedOrders(newSelected)
  }

  const downloadWaybills = async () => {
    if (selectedOrders.size === 0) {
      toast.error('Please select at least one order to download')
      return
    }

    setDownloading(true)
    try {
      const selectedOrderIds = Array.from(selectedOrders)
      const selectedOrderData = orders.filter(o => selectedOrderIds.includes(o.id))

      toast.success(`Preparing ${selectedOrderData.length} waybill(s) for download...`)

      // Collect all waybill HTML content
      const waybillContents = []
      for (const order of selectedOrderData) {
        const wrapperRef = waybillRefs.current[order.id]
        if (wrapperRef) {
          // Clone the waybill content
          const clonedContent = wrapperRef.cloneNode(true)
          waybillContents.push(clonedContent.outerHTML)
        }
      }

      if (waybillContents.length === 0) {
        toast.error('No waybills found to download')
        setDownloading(false)
        return
      }

      // Create a single print window with all waybills
      const printWindow = window.open('', '_blank', 'width=1200,height=800')
      
      if (!printWindow) {
        toast.error('Please allow popups to download waybills')
        setDownloading(false)
        return
      }
      
      // Get all stylesheets
      const linkTags = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
        .map(link => `<link rel="stylesheet" href="${link.href}">`)
        .join('\n')
      
      // Get inline styles from the page
      const styleTags = Array.from(document.querySelectorAll('style'))
        .map(style => `<style>${style.innerHTML}</style>`)
        .join('\n')
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Bulk Waybills - ${selectedOrderData.length} waybill(s)</title>
            ${linkTags}
            ${styleTags}
            <style>
              @media print {
                @page { 
                  margin: 0;
                  size: A4 landscape;
                }
                body { 
                  margin: 0;
                  padding: 0;
                }
                .waybill-page {
                  page-break-after: always;
                  page-break-inside: avoid;
                }
                .waybill-page:last-child {
                  page-break-after: auto;
                }
                * {
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
              }
              body {
                font-family: Arial, sans-serif;
                margin: 0;
                padding: 0;
                background: white;
              }
              .waybill-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                width: 100%;
              }
              .waybill-page {
                margin-bottom: 20px;
              }
            </style>
          </head>
          <body>
            <div class="waybill-container">
              ${waybillContents.map((content, index) => 
                `<div class="waybill-page">${content}</div>`
              ).join('')}
            </div>
          </body>
        </html>
      `)
      
      printWindow.document.close()
      
      // Wait for content to load, then trigger print
      await new Promise((resolve) => {
        const checkLoad = () => {
          if (printWindow.document.readyState === 'complete') {
            // Wait a bit more for images and fonts to load
            setTimeout(() => {
              printWindow.print()
              resolve()
            }, 2000)
          } else {
            setTimeout(checkLoad, 100)
          }
        }
        checkLoad()
        // Fallback timeout
        setTimeout(() => {
          if (printWindow && !printWindow.closed) {
            printWindow.print()
          }
          resolve()
        }, 10000)
      })

      setDownloading(false)
      toast.success(`Successfully prepared ${selectedOrderData.length} waybill(s) in a single PDF. Please save the PDF when prompted.`)
    } catch (error) {
      console.error('Error downloading waybills:', error)
      toast.error('Error downloading waybills: ' + error.message)
      setDownloading(false)
    }
  }

  return (
    <div className='bg-gray-100 flex'>
      <Toaster />
      <Sidebar />
      <div className='h-[100vh] overflow-y-scroll flex flex-1 flex-col'>
        <Topbar />
        <div className='mx-4 my-6'>
          <div className='bg-white rounded-lg shadow-sm p-6'>
            {/* Header */}
            <div className='flex items-center justify-between mb-6'>
              <div>
                <h2 className='text-2xl font-[GilroyBold] text-gray-800'>Bulk Waybill Download</h2>
                <p className='text-gray-600 mt-1'>Select orders to download consignee copy waybills</p>
              </div>
              <div className='flex gap-3'>
                <Button
                  onClick={downloadWaybills}
                  className='bg-[#FFB200] hover:bg-[#e6a000] text-black flex items-center gap-2'
                  disabled={downloading || selectedOrders.size === 0}
                >
                  <FaDownload className='w-4 h-4' />
                  {downloading ? 'Downloading...' : `Download Selected (${selectedOrders.size})`}
                </Button>
                <Button
                  onClick={() => navigate('/orders')}
                  variant='outlined'
                  className='border-gray-300'
                >
                  Back to Orders
                </Button>
              </div>
            </div>

            {/* Select All */}
            <div className='mb-4 flex items-center gap-3'>
              <Checkbox
                checked={selectedOrders.size === orders.length && orders.length > 0}
                onChange={(e) => handleSelectAll(e.target.checked)}
                label={`Select All (${orders.length} orders)`}
              />
              <span className='text-gray-600'>
                {selectedOrders.size} order(s) selected
              </span>
            </div>

            {/* Orders Table */}
            {loading ? (
              <div className='flex justify-center items-center py-20'>
                <Loader />
              </div>
            ) : (
              <div className='overflow-x-auto'>
                <table className='w-full border-collapse bg-white rounded-lg shadow-sm'>
                  <thead>
                    <tr className='bg-black text-white'>
                      <th className='border border-gray-300 px-4 py-3 text-left text-sm font-[GilroyBold] w-12'>
                        <Checkbox
                          checked={selectedOrders.size === orders.length && orders.length > 0}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                      </th>
                      <th className='border border-gray-300 px-4 py-3 text-left text-sm font-[GilroyBold]'>LR No</th>
                      <th className='border border-gray-300 px-4 py-3 text-left text-sm font-[GilroyBold]'>Order Date</th>
                      <th className='border border-gray-300 px-4 py-3 text-left text-sm font-[GilroyBold]'>Client</th>
                      <th className='border border-gray-300 px-4 py-3 text-left text-sm font-[GilroyBold]'>Consignee</th>
                      <th className='border border-gray-300 px-4 py-3 text-left text-sm font-[GilroyBold]'>Delivery</th>
                      <th className='border border-gray-300 px-4 py-3 text-left text-sm font-[GilroyBold]'>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr
                        key={order.id}
                        className={`hover:bg-gray-50 ${selectedOrders.has(order.id) ? 'bg-yellow-50' : ''}`}
                      >
                        <td className='border border-gray-300 px-4 py-2'>
                          <Checkbox
                            checked={selectedOrders.has(order.id)}
                            onChange={(e) => handleSelectOrder(order.id, e.target.checked)}
                          />
                        </td>
                        <td className='border border-gray-300 px-4 py-2 text-sm font-medium text-black hover:text-[#FFB200] cursor-pointer'>
                          {order.lrno}
                        </td>
                        <td className='border border-gray-300 px-4 py-2 text-sm'>
                          {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : ''}
                        </td>
                        <td className='border border-gray-300 px-4 py-2 text-sm'>
                          <div className='text-xs'>
                            <div className='font-medium'>{order.userName || 'N/A'}</div>
                          </div>
                        </td>
                        <td className='border border-gray-300 px-4 py-2 text-sm'>
                          <div className='text-xs'>
                            <div className='font-medium'>{order.dropoff_location?.companyName || 'N/A'}</div>
                            <div className='text-gray-500'>{order.dropoff_location?.city || ''}</div>
                          </div>
                        </td>
                        <td className='border border-gray-300 px-4 py-2 text-sm'>
                          <div className='text-xs'>
                            <div className='font-medium'>{order.dropoff_location?.city || 'N/A'}, {order.dropoff_location?.region || ''}</div>
                            <div className='text-gray-500'>{order.dropoff_location?.zip || ''}</div>
                          </div>
                        </td>
                        <td className='border border-gray-300 px-4 py-2 text-sm'>
                          <span className='px-2 py-1 rounded text-xs bg-gray-200'>
                            Active
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {orders.length === 0 && !loading && (
                  <div className='text-center py-10 text-gray-500'>
                    No orders found
                  </div>
                )}
              </div>
            )}

            {/* Hidden waybill components for printing */}
            <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', visibility: 'hidden', width: '1200px' }}>
              {orders.map(order => (
                <div key={order.id} ref={(el) => {
                  if (el) {
                    waybillRefs.current[order.id] = el
                  }
                }}>
                  <RecieverCopyContent id={order.lrno} orderDetail={order} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BulkWaybillDownload
